import styled, { x } from '@xstyled/styled-components';
import React from 'react';
import DoneIcon from '~/assets/icons/white_done.svg';
import LockIcon from '~/assets/icons/white_lock.svg';
import { useNavDistance } from '~/training/state/useNavDistance';
import { useNextSequence } from '~/training/useNextSequence';
import { Card } from '~/ui/card/Card';
import { Flex } from '~/ui/flex/Flex';
import { CHAPTER_SELECTION, TEXT } from '..';
import {
  Item,
  ItemDefinition,
  ItemValidationError,
  registry,
  RenderProps
} from '../Item';
import { CHAPTER, IMAGE } from '../itemIds';

const Lock = styled(LockIcon)({
  maxWidth: '50%',
  maxHeight: '50%'
});

const Done = styled(DoneIcon)({
  maxWidth: '50%',
  maxHeight: '50%'
});

interface Props {
  value: string;
}

const acceptedChildren = [TEXT, IMAGE];

export type ChapterDefinition = ItemDefinition<typeof CHAPTER, Props>;

export class Chapter extends Item<typeof CHAPTER, Props> {
  validate() {
    if (this.parent && this.parent.type !== CHAPTER_SELECTION) {
      throw new ItemValidationError(
        `An item of type [${CHAPTER}] can only be a child of an item of type [${CHAPTER_SELECTION}]`
      );
    }

    if (this.definition.items.some(i => !acceptedChildren.includes(i.type))) {
      throw new ItemValidationError(
        `An item of type [${CHAPTER}] can only have children of type [${acceptedChildren.join(
          ', '
        )}]`
      );
    }
  }

  protected render({ value, self }: RenderProps<Props>) {
    const distance = useNavDistance(value);
    const imageItem = self.children?.find(i => i.type === IMAGE);
    const children = self.children?.filter(i => i !== imageItem);
    const next = useNextSequence();

    const ImageComp = imageItem?.component();

    return (
      <x.button
        as={Card}
        role="button"
        w={{ _: '100%', md: '280px' }}
        h={{ _: '97px', md: '376px' }}
        display="grid"
        gridTemplateRows={{ _: '1fr', md: '1fr auto' }}
        gridTemplateColumns={{ _: '126px 1fr', md: '1fr' }}
        overflow="hidden"
        // disabled={distance !== 2}
        cursor={distance === 2 ? 'pointer' : 'default'}
        onClick={() => {
          if (distance !== 2) return false;
          if (isNaN(distance)) return;
          next();
        }}
      >
        {ImageComp && (
          <ImageComp>
            {distance < 0 && (
              <Flex
                variant="center"
                position="absolute"
                backgroundColor="rgba(152, 69, 251, 0.85)"
              >
                <Done />
              </Flex>
            )}
            {distance > 2 && (
              <Flex
                variant="center"
                position="absolute"
                backgroundColor="rgba(0, 0, 0, 0.6)"
              >
                <Lock />
              </Flex>
            )}
          </ImageComp>
        )}
        {children?.length && (
          <x.div
            p="md"
            gap="md"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            {children?.map(c => {
              const Comp = c.component();
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              return <Comp key={c.id} type={c.type} />;
            })}
          </x.div>
        )}
      </x.button>
    );
  }
}

registry[CHAPTER] = Chapter;
